import { useCallback, useMemo, useState } from "react";

import type { GaConnection } from "@similarweb/ga-connect";
import { useTranslate } from "@similarweb/i18n-provider";
import { GoogleSearch } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { DropdownButton, CheckableDropdownItem, Dropdown } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { ConfirmModal } from "./ConfirmModal";
import { AccountDropdownFooter, DropdownWrapper, DropdownItem, ErrorMessage, Label, ReconnectAction } from "./styles";
import { dropdownTextSearchFilter } from "./utils";

type Props = {
    connections: GaConnection[];
    currentConnection: GaConnection | null;
    onSelect: (connection: GaConnection) => void;
    onConnectNewAccount: () => void;
    onReconnect?: () => void;
    isLoading?: boolean;
    requireConfirmation?: boolean;
    hasError?: boolean;
    errorMessage?: string;
};

export const AccountPicker = ({
    connections,
    currentConnection,
    onSelect,
    onConnectNewAccount,
    onReconnect,
    isLoading = false,
    requireConfirmation = false,
    hasError = false,
    errorMessage,
}: Props) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);
    const [pendingConnection, setPendingConnection] = useState<GaConnection | null>(null);

    const handleSelect = useCallback(
        (item: { id: string }) => {
            const connection = connections.find((c) => String(c.connectionId) === item.id);
            if (!connection) {
                return;
            }

            if (requireConfirmation && connection.connectionId !== currentConnection?.connectionId) {
                setPendingConnection(connection);
                return;
            }

            onSelect(connection);
        },
        [connections, currentConnection, onSelect, requireConfirmation],
    );

    const handleConfirm = useCallback(() => {
        if (pendingConnection) {
            onSelect(pendingConnection);
        }
        setPendingConnection(null);
    }, [pendingConnection, onSelect]);

    const handleCancel = useCallback(() => setPendingConnection(null), []);

    const dropdownItems = useMemo(
        () =>
            connections.map((connection) => (
                <CheckableDropdownItem
                    key={connection.connectionId}
                    id={String(connection.connectionId)}
                    text={connection.connectionName}
                    selected={connection.connectionId === currentConnection?.connectionId}
                    data-automation={`account-item-${connection.connectionName}`}
                >
                    <DropdownItem>{connection.connectionName}</DropdownItem>
                </CheckableDropdownItem>
            )),
        [connections, currentConnection],
    );

    const footer = useCallback(
        () => (
            <AccountDropdownFooter
                data-automation="connect-another-account"
                onClick={onConnectNewAccount}
            >
                + {translate("rankTracker.gsc.accountPicker.connectAnother")}
            </AccountDropdownFooter>
        ),
        [onConnectNewAccount, translate],
    );

    const placeholder = translate("rankTracker.gsc.accountPicker.placeholder");

    return (
        <>
            <DropdownWrapper
                data-automation="account-picker"
                hasValue={!!currentConnection}
                hasError={hasError}
            >
                <Label>{translate("rankTracker.gsc.accountPicker.label")}</Label>
                <Dropdown
                    isOpen={isOpen}
                    appendTo="body"
                    dropdownPopupPlacement="ontop-left"
                    hasSearch
                    searchPlaceHolder={placeholder}
                    searchFilter={dropdownTextSearchFilter}
                    onToggle={setIsOpen}
                    onClick={handleSelect}
                    closeOnItemClick
                    footerComponent={footer}
                    disabled={isLoading}
                >
                    {[
                        <DropdownButton
                            key="trigger"
                            height={40}
                            disabled={isLoading}
                        >
                            <DropdownItem>
                                <GoogleSearch
                                    size="sm"
                                    color={colorsPalettes.carbon[200]}
                                />
                                {currentConnection?.connectionName ?? placeholder}
                            </DropdownItem>
                        </DropdownButton>,
                        ...dropdownItems,
                    ]}
                </Dropdown>
                {errorMessage || onReconnect ? (
                    <Flex
                        alignItems="baseline"
                        gap="8px"
                    >
                        {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}
                        {onReconnect ? (
                            <ReconnectAction
                                data-automation="gsc-account-reconnect"
                                onClick={onReconnect}
                            >
                                {translate("rankTracker.gsc.accountPicker.reconnectAction", "Reconnect")}
                            </ReconnectAction>
                        ) : null}
                    </Flex>
                ) : null}
            </DropdownWrapper>
            <ConfirmModal
                isOpen={!!pendingConnection}
                description={translate("rankTracker.gsc.confirmModal.account.description")}
                onCancel={handleCancel}
                onConfirm={handleConfirm}
            />
        </>
    );
};
